import { useState, useEffect, useMemo } from "react";
import { DEFAULT_LIMIT_ITEMS } from "constants/data";
import { Category } from "../types";
import getCategoryListApi, { CategoryQuery } from "../api/categoryApi";

declare global {
  interface ObjectConstructor {
    /**
     * Groups members of an iterable according to the return value of the passed callback.
     * @param items An iterable.
     * @param keySelector A callback which will be invoked for each item in items.
     */
    groupBy<K extends PropertyKey, T>(
      items: Iterable<T>,
      keySelector: (item: T, index: number) => K,
    ): Partial<Record<K, T[]>>;
  }
}

/**
 * Custom hook to fetch and manage a paginated list of companies.
 * This hook uses a placeholder for the actual API call. Replace the commented-out fetchClients function with the actual API call to fetch companies.
 */
const useCategoryList = () => {
  const [queryParams, setQueryParams] = useState<Partial<CategoryQuery>>({
    categoryKbn: "",
    categoryName: "",
    statusFlag: 0,
  });
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState<Category[]>([]);
  const [groups, setGroups] = useState<{
    names: string[];
    listKbn: string[];
  }>({
    names: [],
    listKbn: [],
  });

  const totalPage = useMemo(() => Math.ceil(total / DEFAULT_LIMIT_ITEMS), [total]);

  /**
   * @param body CompanyQuery
   */
  const getCategories = async (body?: CategoryQuery) => {
    try {
      const prevScrollY = document.documentElement.scrollTop || document.body.scrollTop;
      const data = await getCategoryListApi(body);
      setCategories((prev) => (body && body?.offset > 1 ? [...prev, ...data.list] : data.list));
      setTotal(data.total);
      window.scrollTo(0, prevScrollY || 0);
    } catch (error) {
      // do nothing
    }
    setLoading(false);
  };

  const fetchGroups = async () => {
    const data = await getCategoryListApi({
      limit: 9999,
      offset: 1,
    });
    setGroups((value) => ({
      ...value,
      listKbn: Object.keys(Object.groupBy(data.list, ({ category_kbn }) => category_kbn)),
      names: Object.keys(Object.groupBy(data.list, ({ category_name }) => category_name)),
    }));
  };

  useEffect(() => {
    getCategories({ offset: 1 });
    fetchGroups();
  }, []);

  const handleSearch = () => {
    setPage(1);
    getCategories({
      ...queryParams,
      offset: 1,
    });
  };

  const onLoadMore = async () => {
    if (page + 1 > totalPage) return;
    setLoading(true);
    setPage((prevPage) => prevPage + 1);
    await getCategories({ ...queryParams, offset: page + 1 });
  };

  return {
    categories,
    queryParams,
    total,
    totalPage,
    page,
    loading,
    groups,
    handleSearch,
    onLoadMore,
    setQueryParams,
  };
};

export default useCategoryList;
